/**
 * useProjectByShortId — resolve a human-readable project short_id (URL slug)
 * into the full project payload.
 *
 * Backed by `GET /api/v1/projects/by-short-id/:shortId`, which returns the
 * standard `{ success, data }` envelope. The hook unwraps `data` and exposes
 * a stable `notFound` boolean so consumers (e.g. ProjectNotFoundPage) can
 * distinguish a 404 from a transient failure without inspecting error
 * message strings.
 *
 * Cache: 5 min staleTime, `refetchOnWindowFocus: false` — project metadata
 * (name, org_id, snapshot, etc.) is rarely changed outside the project;
 * `useProjectSnapshot` derives from this cache when updates arrive via
 * n8n.
 */

import { useQuery } from '@tanstack/react-query';

const FIVE_MINUTES = 5 * 60 * 1000;

export interface ProjectByShortId {
  id: string;
  short_id: string;
  name: string;
  org_id?: string;
  // Other project columns are tolerated; consumers should opt-in via narrowing.
  [key: string]: unknown;
}

/**
 * Plain (non-discriminated) shape exposing only the fields callers actually
 * use. Previously this extended `UseQueryResult<…>`, which is a discriminated
 * union (success / error / loading variants); intersecting that union with
 * `{ notFound: boolean }` collapses the discrimination and breaks direct
 * `data` / `isLoading` / `error` access at consumer sites — three
 * `@ts-expect-error` directives used to mask the fallout. If a caller needs
 * the full UseQueryResult surface (refetch, isFetching, status …), use
 * `useQuery` directly instead of widening this contract.
 */
export interface UseProjectByShortIdResult {
  data: ProjectByShortId | undefined;
  isLoading: boolean;
  error: Error | null;
  notFound: boolean;
}

class ProjectFetchError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProjectFetchError';
    this.status = status;
  }
}

export function useProjectByShortId(shortId: string | null | undefined): UseProjectByShortIdResult {
  const query = useQuery<ProjectByShortId, Error>({
    queryKey: ['v2', 'projectByShortId', shortId] as const,
    enabled: Boolean(shortId),
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    // A 401 here is an auth-init race (cookie refresh still settling), not a
    // real failure — retry a couple of times before surfacing an error. A 404
    // (ProjectFetchError status 404 → `notFound`) and other errors are never
    // retried, so the dedicated not-found page still shows immediately.
    retry: (failureCount, error) =>
      (error as Error & { status?: number })?.status === 401 && failureCount < 2,
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 2000),
    queryFn: async () => {
      // encodeURIComponent guards against shortIds that contain '/', '?', '#',
      // or other reserved characters — they would otherwise break path parsing
      // or, worse, route to a different endpoint entirely.
      const res = await fetch(`/api/v1/projects/by-short-id/${encodeURIComponent(shortId!)}`, {
        credentials: 'include',
      });

      if (res.status === 404) {
        throw new ProjectFetchError('Project not found', 404);
      }
      if (!res.ok) {
        throw new ProjectFetchError(`Project fetch failed: ${res.status}`, res.status);
      }

      const body = (await res.json()) as { success?: boolean; data?: ProjectByShortId } | null;

      // Strict envelope check. We do not silently accept bare payloads — a
      // missing `data` field signals a contract drift and should surface as
      // an error rather than masquerading as a successful empty project.
      if (!body || typeof body !== 'object' || !('data' in body) || !body.data) {
        throw new ProjectFetchError('Malformed response: missing data envelope', 502);
      }
      return body.data;
    },
  });

  const err = query.error as (Error & { status?: number }) | null;
  const notFound = !!err && err.status === 404;

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ?? null,
    notFound,
  };
}
